import { Box, Dialog, useTheme } from "arkh";
import React, { useCallback, useMemo, useState } from "react";
import DateTimePicker, {
  DateTimePickerEvent,
} from "@react-native-community/datetimepicker";

import { graphql, useMutation } from "react-relay";
import { Platform } from "react-native";
import { ExpireDatePickerMutation } from "./__generated__/ExpireDatePickerMutation.graphql";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";

import { DateTime } from "luxon";
import { OrgButton } from "../OrgHome/OrgButton";
import { WebDateSelector } from "../../components/mobile/WebDateSelector/WebDateSelector";
import { FormattedMessage } from "react-intl";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";

type ActionProps = {
  mode: "edit" | "create";
  initialDate?: string;
  setDate: (date: string) => void;
  onCancel: () => void;
};

function ExpireDatePickerAndroid(props: ActionProps) {
  const [ignoreChangeEvents, setIgnoreChangeEvents] = React.useState(false);
  const today = useMemo(() => new Date(), []);
  const intl_tokens = useIntlTokens();
  const theme = useTheme();
  const onChange = useCallback(
    (event: DateTimePickerEvent, selectedDate: Date | undefined) => {
      if (ignoreChangeEvents) {
        return;
      }
      switch (event.type) {
        case "set":
          if (selectedDate) {
            setIgnoreChangeEvents(true);
            props.setDate(selectedDate.toISOString());
          }
          break;
        case "dismissed":
          props.onCancel();
          break;
      }
    },
    [props, ignoreChangeEvents]
  );
  return (
    <DateTimePicker
      minimumDate={today}
      value={
        props.initialDate
          ? DateTime.fromISO(props.initialDate).toJSDate()
          : today
      }
      mode="date"
      display="calendar"
      positiveButton={{
        label: props.mode === "edit" ? intl_tokens.Update : intl_tokens.Add,
        textColor: theme.primary,
      }}
      negativeButton={{
        label: intl_tokens.Cancel,
        textColor: theme["text-secondary"],
      }}
      onChange={onChange}
    />
  );
}

function ExpireDatePickerRest(
  props: {
    visible: boolean;
  } & ActionProps
) {
  const intl_tokens = useIntlTokens();
  const [date, setDate] = useState(
    props.initialDate
      ? DateTime.fromISO(props.initialDate).toJSDate()
      : DateTime.now().toJSDate()
  );
  const today = useMemo(() => DateTime.now(), []);
  return (
    <Dialog
      visible={props.visible}
      title={
        props.mode === "edit" ? (
          intl_tokens.ChangeExpireDate
        ) : (
          <FormattedMessage
            description="ProductDateTracker add expire date"
            defaultMessage="Add Expire Date"
          />
        )
      }
    >
      <Box
        style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        spaceHorizontal="medium"
      >
        {Platform.OS === "web" ? (
          <WebDateSelector
            type="date"
            minDate={today}
            value={date ? DateTime.fromJSDate(date) : null}
            onChangeValue={(selectedDate) => {
              if (selectedDate) {
                const date = selectedDate.toJSDate();
                setDate(date);
              }
            }}
          />
        ) : (
          <DateTimePicker
            themeVariant="light"
            value={date}
            mode="date"
            display="compact"
            minimumDate={new Date()}
            onChange={(_, selectedDate) => {
              if (selectedDate) {
                setDate(selectedDate);
              }
            }}
          />
        )}
      </Box>
      <Box
        space="medium"
        style={{
          flexDirection: "row",
          alignItems: "center",
        }}
        color="surface-primary"
        shadow="strong"
      >
        <OrgButton
          title={intl_tokens.Cancel}
          style={{ flex: 1 }}
          onPress={() => {
            props.onCancel();
          }}
          backgroundColor="status-negative"
          tintColor="text-inverted"
        />
        <Box spaceRight="medium" />
        <OrgButton
          style={{ flex: 1 }}
          title={props.mode === "edit" ? intl_tokens.Update : intl_tokens.Add}
          backgroundColor="primary"
          tintColor="text-inverted"
          onPress={() => {
            props.setDate(date.toISOString());
          }}
        />
      </Box>
    </Dialog>
  );
}

export function ExpireDatePickerEdit({
  productId,
  batchId,
  initialDate,
  onCompleted,
  onClose,
  visible,
}: {
  productId: string;
  batchId: string;
  initialDate: string;
  onCompleted: () => void;
  onClose: () => void;
  visible: boolean;
}) {
  const [changeDate] = useMutation(graphql`
    mutation ExpireDatePickerEditMutation(
      $productId: ID!
      $batchId: ID!
      $date: String!
    ) {
      storeProduct(id: $productId) {
        expireBatch(id: $batchId) {
          changeDate(date: $date) {
            id
            product {
              id
              hasPendingExpireDateActions
              hasExpiredExpireDates
            }
            ...ProductExpireDateListItem_StoreProductExpireBatch
            ...ProductExpireBatch_ProductExpireBatch
            actions {
              id
              startAtDate {
                isoString
              }
            }
          }
        }
      }
    }
  `);

  const onCreateDate = useCallback(
    (date: string) => {
      onClose();
      changeDate({
        variables: {
          productId,
          batchId,
          date,
        },
        onError: (error) => {
          console.log(JSON.stringify({ error }, null, 2));
        },
        onCompleted: () => {
          onCompleted();
        },
      });
    },
    [onClose, changeDate, productId, batchId, onCompleted]
  );
  if (!visible) {
    return null;
  }

  if (Platform.OS === "android") {
    return (
      <ExpireDatePickerAndroid
        mode="edit"
        setDate={onCreateDate}
        onCancel={onClose}
        initialDate={initialDate}
      />
    );
  }

  return (
    <ExpireDatePickerRest
      visible={visible}
      setDate={onCreateDate}
      onCancel={onClose}
      mode="edit"
      initialDate={initialDate}
    />
  );
}

export function ExpireDatePicker({
  productId,
  onCompleted,
  onClose,
  visible,
}: {
  productId: string;
  onCompleted: () => void;
  onClose: () => void;
  visible: boolean;
}) {
  const { currentEmployeeId } = usePersistedEmployeeId();
  const [createDate] = useMutation<ExpireDatePickerMutation>(graphql`
    mutation ExpireDatePickerMutation(
      $productId: ID!
      $expireAt: String!
      $employeeId: ID
    ) {
      storeProduct(id: $productId) {
        createExpireBatch(expireAt: $expireAt, employeeId: $employeeId) {
          id
          hasPendingExpireDateActions
          hasExpiredExpireDates
          activeExpireBatches {
            id
            ...ProductExpireDateListItem_StoreProductExpireBatch
          }
        }
      }
    }
  `);

  const onCreateDate = useCallback(
    (date: string) => {
      onClose();
      createDate({
        variables: {
          productId,
          expireAt: date,
          employeeId: currentEmployeeId,
        },
        onError: (error) => {
          console.log(JSON.stringify({ error }, null, 2));
        },
        onCompleted: () => {
          onCompleted();
        },
      });
    },
    [productId, onCompleted, createDate, onClose, currentEmployeeId]
  );

  if (!visible) {
    return null;
  }

  if (Platform.OS === "android") {
    return (
      <ExpireDatePickerAndroid
        mode="create"
        setDate={onCreateDate}
        onCancel={onClose}
      />
    );
  }

  return (
    <ExpireDatePickerRest
      mode="create"
      visible={visible}
      setDate={onCreateDate}
      onCancel={onClose}
    />
  );
}
